import React, { useEffect, useState } from 'react'
import { useEvent } from '@/hooks/useEvent'
import { supabase } from '@/lib/supabaseClient'
import type { AuditLogRow, CompanyRow } from '@/lib/types'
import { Badge, Card, EmptyState, PageSpinner, Select } from '@/components/ui'
import { formatDateTime, formatRelative } from '@/lib/format'

interface LogWithCompany extends AuditLogRow {
  companies?: { name: string } | null
}

/** Event-wide audit trail (section 51-54): every admin, company and system write, in one chronological, human-readable timeline. */
export default function Activity() {
  const { event } = useEvent()
  const [logs, setLogs] = useState<LogWithCompany[] | null>(null)
  const [companies, setCompanies] = useState<CompanyRow[]>([])
  const [companyId, setCompanyId] = useState('')
  const [actorType, setActorType] = useState('')

  useEffect(() => {
    supabase
      .from('companies')
      .select('*')
      .eq('event_id', event.id)
      .order('name')
      .then(({ data }) => setCompanies((data as CompanyRow[]) || []))
  }, [event.id])

  useEffect(() => {
    (async () => {
      setLogs(null)
      let query = supabase.from('audit_logs').select('*, companies(name)').eq('event_id', event.id).order('created_at', { ascending: false }).limit(300)
      if (companyId) query = query.eq('company_id', companyId)
      if (actorType) query = query.eq('actor_type', actorType)
      const { data } = await query
      setLogs((data as LogWithCompany[]) || [])
    })()
  }, [event.id, companyId, actorType])

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-slate-900">Activity Log</h1>
        <p className="text-sm text-slate-500">Every change made across this event, most recent first.</p>
      </div>

      <Card className="mb-4 flex flex-wrap items-end gap-3 p-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Company</label>
          <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="min-w-[180px]">
            <option value="">All companies</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Actor</label>
          <Select value={actorType} onChange={(e) => setActorType(e.target.value)} className="min-w-[140px]">
            <option value="">Everyone</option>
            <option value="admin">Admin</option>
            <option value="company">Company</option>
            <option value="import">Import</option>
            <option value="system">System</option>
          </Select>
        </div>
      </Card>

      {logs === null ? (
        <PageSpinner label="Loading activity…" />
      ) : logs.length === 0 ? (
        <EmptyState title="No activity recorded yet" />
      ) : (
        <Card className="p-4">
          <ol className="space-y-3">
            {logs.map((log) => (
              <li key={log.id} className="flex gap-3 border-b border-slate-100 pb-3 text-sm last:border-b-0 last:pb-0">
                <div className="w-24 shrink-0 text-xs text-slate-400" title={formatDateTime(log.created_at)}>
                  {formatRelative(log.created_at)}
                </div>
                <div className="flex flex-1 flex-wrap items-center gap-2">
                  <span className="text-slate-700">{log.description}</span>
                  {log.companies?.name && <Badge tone="blue">{log.companies.name}</Badge>}
                  <Badge tone={log.actor_type === 'admin' ? 'blue' : log.actor_type === 'company' ? 'green' : log.actor_type === 'import' ? 'amber' : 'slate'}>{log.actor_type}</Badge>
                </div>
              </li>
            ))}
          </ol>
        </Card>
      )}
    </div>
  )
}
